/**
 * vertexEdit — AK-322 «Редактирование вершин контура».
 *
 * Чистая геометрия правки кольца полигона (без terra-draw/MapLibre/DOM):
 *   - deleteVertexFromRing — удалить вершину по индексу, сохранив замкнутость
 *     кольца (first==last) и не дав полигону выродиться (<3 уникальных вершины);
 *   - findNearestVertexIndex — экранный hit-test: индекс ближайшей вершины
 *     кольца в пиксельном пороге от курсора (для dblclick-удаления).
 *
 * Зачем отдельный модуль (ADR-038): добавление точки на ребре полностью владеет
 * terra-draw (midpoint click/drag в select-mode), а удаление вершины founder
 * хочет жестом double-click — это НЕ нативный жест terra-draw (там delete на
 * right-click), поэтому ловим dblclick сами и мутируем кольцо через эту чистую
 * функцию, прогоняя результат через тот же change→recompute→history путь
 * (значит — undoable). Чистота даёт юнит-тесты без карты.
 */
import type { LngLat, FieldGeometry } from './fieldGeojson';
import type { ScreenPt } from './snap';

/**
 * Минимум УНИКАЛЬНЫХ вершин у валидного полигона — 3. Замкнутое кольцо хранит
 * дублирующую первую точку в конце, поэтому валидная длина кольца — ≥4.
 */
export const MIN_RING_LENGTH = 4;

/** Результат удаления вершины. */
export interface DeleteVertexResult {
  /** Новое кольцо (замкнутое) либо null, если удаление запрещено (выродило бы полигон). */
  ring: LngLat[] | null;
  /** Почему no-op (для лёгкого фидбэка в панели). */
  reason?: 'min-vertices' | 'out-of-range';
}

/**
 * Удалить вершину `idx` из замкнутого кольца, сохранив замкнутость и не дав
 * полигону выродиться.
 *
 * Семантика индексов: кольцо замкнуто (ring[0] == ring[last]). Индекс 0 и
 * last — это ОДНА И ТА ЖЕ точка (стык кольца). Удаление любого из них убирает
 * стыковую вершину и заново замыкает кольцо на новую первую точку — поведение
 * совпадает с terra-draw deleteCoordinate для polygon (shift+pop+push).
 *
 * Guard: если после удаления уникальных вершин осталось бы <3 (т.е. длина
 * замкнутого кольца стала бы <4) — НЕ удаляем, возвращаем reason='min-vertices'.
 */
export function deleteVertexFromRing(ring: LngLat[], idx: number): DeleteVertexResult {
  const n = ring.length;
  if (n < MIN_RING_LENGTH) {
    // Уже на минимуме — любое удаление выродит полигон.
    return { ring: null, reason: 'min-vertices' };
  }
  if (idx < 0 || idx >= n) {
    return { ring: null, reason: 'out-of-range' };
  }
  // Удаление одной вершины уменьшает длину на 1. Замкнутому кольцу нужна
  // длина ≥4 (≥3 уникальных). Значит удалять можно только если n-1 ≥ 4 → n ≥ 5.
  if (n - 1 < MIN_RING_LENGTH) {
    return { ring: null, reason: 'min-vertices' };
  }

  const last = n - 1;
  const isClosed = ring[0][0] === ring[last][0] && ring[0][1] === ring[last][1];

  if (isClosed && (idx === 0 || idx === last)) {
    // Удаляем стыковую вершину: убираем старую первую/последнюю и замыкаем на
    // новую первую (как terra-draw: shift, pop, push([new first])).
    const next = ring.slice(1, last); // без старой первой и старой последней-дубля
    next.push([next[0][0], next[0][1]]);
    return { ring: next };
  }

  const next = ring.slice();
  next.splice(idx, 1);
  // Если кольцо было замкнуто и мы удалили НЕ стыковую точку — оно остаётся
  // замкнутым (стыковая пара не тронута). Подстрахуемся на всякий случай.
  if (isClosed) {
    const l = next.length - 1;
    if (next[0][0] !== next[l][0] || next[0][1] !== next[l][1]) {
      next.push([next[0][0], next[0][1]]);
    }
  }
  return { ring: next };
}

/**
 * Индекс ближайшей вершины кольца в пиксельном пороге от курсора, или null.
 * Стыковую дублирующую вершину (last==first у замкнутого кольца) НЕ возвращаем
 * отдельно — индекс 0 представляет стык. project конвертирует lng/lat→экран.
 */
export function findNearestVertexIndex(
  ring: LngLat[],
  cursor: ScreenPt,
  project: (ll: LngLat) => ScreenPt,
  thresholdPx: number,
): number | null {
  const n = ring.length;
  if (n === 0) return null;
  const isClosed =
    n >= 2 && ring[0][0] === ring[n - 1][0] && ring[0][1] === ring[n - 1][1];
  // У замкнутого кольца последний == первый; не перебираем дубль, чтобы 0 и
  // last не конкурировали — индекс 0 «выигрывает» стык.
  const upper = isClosed ? n - 1 : n;
  let best: number | null = null;
  let bestDist = Infinity;
  for (let i = 0; i < upper; i++) {
    const p = project(ring[i]);
    const d = Math.hypot(p.x - cursor.x, p.y - cursor.y);
    if (d <= thresholdPx && d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/** Узкий тип — только то, что нужно для hit-теста (упрощает тесты). */
export type RingSource = Pick<FieldGeometry, 'primaryRing'>;
